// ** React Imports
import { useContext, useEffect, useMemo, useState } from "react";

// ** Redux & Store & Actions
import { useSelector } from "react-redux";

// ** Third Party Components
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import Dropdown from "components/layout/Dropdown";

// ** Selector Components
import SearchInput from "./components/SearchInput";
import MenuItems from "./components/MenuItems";
import Activator from "./components/Activator";
import { DropdownTriggerContext } from "components/layout/Dropdown/DropdownTriggerContext";
import { StyledSelectorLabel } from "./components/SelectorLabel.styled.";
import { NoResultItem } from "./components/NoResultItem";

// ** Utils & Helpers
import { publish, subscribe, unsubscribe } from "helpers/events";
import {
	ACTUAL_OPEN_SELECTOR,
	IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE,
} from "helpers/eventsList";
import slug from "utils/slug";

// ** Custom Hooks
import useOpenClose from "hooks/useOpenClose";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const SelectorSelectSingle = ({
	selectorKey,
	label,
	items,
	customActivator,
	customDropdownList,
	activatorStyle,
	activatorStyleOptions,
	activatorWrapperStyle,
	customLayerStyle,
	emptyText: userEmptyText,
	selected,
	onAccept,
	menuFontSize,
	withMobileVersion,
	withSearchInput,
	useLayerOptions,
	maxLayerHeight = 300,
	subscribeActualOpenSelectorEvent,
	disabled,
	alwaysLight = false,
	customMobileLayerStyle,
	isKeyboardOpen,
	visualViewportHeight,
	isSingleDropdown,
	isMultiDropdown,
	preventEnableScrollInMobile,
	autoFocus,
	withInputReset = false,
	withFullItem = false,
	customInput = null,
	customInputWidth = null,
	minWidth,
	dropdownState = null,
	isLanding = false,
	closeWithoutEnableScroll = false,
	customWidthWhenDropdownOpen,
}) => {
	const { t } = useTranslation(["common"]);
	const { isPhone } = useContext(deviceTypeContext);
	const language = useSelector((state) => state.globalCommon.language);
	const { isOpen, close, toggle } = useOpenClose();

	const extendedClose = ({ insideUseEffect = false } = {}) => {
		if (insideUseEffect || closeWithoutEnableScroll) {
			close();
		} else {
			close(() => {
				if (
					document.body.style.overflowY === "hidden" &&
					!preventEnableScrollInMobile &&
					(isSingleDropdown || !customMobileLayerStyle)
				) {
					document.body.style.overflowY = "scroll";
				}
			});
		}
	};

	const extendedToggle = () => {
		toggle(() => {
			if (
				!isOpen &&
				isPhone &&
				document.body.style.overflowY !== "hidden"
			) {
				document.body.style.overflowY = "hidden";
			} else if (
				isOpen &&
				document.body.style.overflowY === "hidden" &&
				!closeWithoutEnableScroll
			) {
				document.body.style.overflowY = "scroll";
			}
		});
	};

	// ** Dropdown opcje konfiguracyjne
	const useLayerOptionsFinal = {
		overflowContainer: false,
		...useLayerOptions,
	};

	const customLayerStyleFinal = {
		maxHeight: `${maxLayerHeight}px`,
		zIndex: isPhone ? 1000 : 499,
		...customLayerStyle,
	};

	// ** Hook useState - wartość wyszukiwana i funkcja ją ustawiająca
	const [searchValue, setSearchValue] = useState("");

	// ** Hook useState - wartość ustawiana na true w przypadu gdy inny selektor znajduje
	// ** się w stanie 'onAccept', aby nie pozwolić na otwarcie kolejnego selektora
	const [isOtherSelectorInAcceptMode, setOtherSelectorInAcceptMode] =
		useState(false);

	// ** Tekst wyświetlany w przypadku nie wybranie elementu
	const emptyText =
		userEmptyText || userEmptyText === ""
			? userEmptyText
			: t("common:choose");

	// ** Hook useMemo - Ostateczna lista elementów wyświetlanych w dropdown'ie
	const finalItems = useMemo(() => {
		return items.map((item, index) => ({
			...item,
			key: item.key || `item-${index}`,
		}));
	}, [items, language]);

	// ** Hook useMemo - Aktualnie wybrany element
	const selectedItem = useMemo(() => {
		if (selected === 0) {
			return finalItems.filter((item) => item.value === 0)[0];
		} else if (selected) {
			return finalItems.find((item) => item.value === selected);
		} else {
			return null;
		}
	}, [finalItems, selected, language]);

	// ** Informacja o tym czy wybrano jakiś element z listy
	const isSelected = Boolean(selectedItem);

	// ** Content wyświetlany w aktywatorze - wybrany element z listy
	const contentToDisplay = isSelected ? selectedItem.content : emptyText;

	// ** Funkcja obsługująca kliknięcie w dany element z listy
	const handleSelectItem = (item) => {
		if (item.value != selected) {
			extendedClose();
			if (typeof onAccept === "function") {
				if (typeof item?.value === "number") {
					!withFullItem
						? onAccept(item.value, selected)
						: onAccept(item, selected);
				} else {
					!withFullItem
						? onAccept(item.value || null, selected)
						: onAccept(item || null, selected);
				}
			}
			if (item.onClick) {
				item.onClick();
			}
		} else {
			extendedClose();
			if (typeof onAccept === "function") {
				onAccept(null, selected);
			}
			if (item.onClick) {
				item.onClick();
			}
		}
	};

	// ** Efekt publikujący CustomEvent ACTUAL_OPEN_SELECTOR
	// ** z 'id' (label) aktualnie otwartego selektora.
	useEffect(() => {
		if (isOpen) {
			publish(ACTUAL_OPEN_SELECTOR, { selectorKey });
		}
	}, [isOpen, isPhone]);

	// ** Efekt subskrybujący event ACTUAL_OPEN_SELECTOR
	// ** W przypadku gdy 'id' nowo otwartego selektora nie zgada się z 'id'
	// ** bieżacego komponentu jest on zamykany.
	useEffect(() => {
		if (subscribeActualOpenSelectorEvent) {
			subscribe(ACTUAL_OPEN_SELECTOR, (event) => {
				if (event.detail.selectorKey !== selectorKey) {
					extendedClose({ insideUseEffect: true });
				}
			});
		}

		// ** Subskrypcja event'u 'IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE'
		subscribe(IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE, (event) => {
			setOtherSelectorInAcceptMode(event.detail);
		});

		return () => {
			unsubscribe(ACTUAL_OPEN_SELECTOR);
			unsubscribe(IS_MULTIPLE_SELECTOR_IN_ACCEPT_MODE);
		};
	}, []);

	useEffect(() => {
		if (!isOpen && searchValue.length > 0 && withInputReset) {
			setSearchValue("");
		}

		if (typeof dropdownState === "function") {
			dropdownState(isOpen);
		}
	}, [isOpen]);

	// ** Wyświetlanie komunikatu "Brak wyników".
	const searchSlug = useMemo(() => {
		return searchValue.length > 0
			? slug(searchValue.toLowerCase(), " ")
			: "";
	}, [searchValue]);

	const filteredItems = useMemo(() => {
		if (!searchSlug || !withSearchInput) return items;

		return items.filter((item) => {
			const itemSlug = slug(
				item.searchContent || item.content,
				" "
			).toLowerCase();
			return itemSlug.includes(searchSlug);
		});
	}, [items, searchSlug]);

	// #####################################################

	return (
		<Block
			mb={activatorStyleOptions?.marginBottomValue}
			style={{
				flex: activatorStyleOptions?.flex1 ? "1" : "unset",
				width:
					isPhone && !activatorStyleOptions?.noFullWidth
						? "100%"
						: "unset",
			}}
		>
			<Dropdown
				isOpen={isOpen}
				close={(insideUseEffect) => {
					extendedClose(insideUseEffect);
				}}
				useLayerOptions={useLayerOptionsFinal}
				customLayerStyle={customLayerStyleFinal}
				withMobileVersion={withMobileVersion}
				mobileTitle={label}
				alwaysLight={alwaysLight}
				customMobileLayerStyle={customMobileLayerStyle}
				isKeyboardOpen={isKeyboardOpen}
				visualViewportHeight={visualViewportHeight}
				isSingleDropdown={isSingleDropdown}
				isMultiDropdown={isMultiDropdown}
				minWidth={minWidth || customInputWidth}
				autoBorder={true}
				isLanding={isLanding}
				menuContent={({ layerSide }) =>
					isOpen && typeof customDropdownList === "function" ? (
						customDropdownList({ items: finalItems })
					) : (
						<Block
							full
							width="100%"
							style={{
								height: "100%",
								maxHeight: isPhone
									? "70vh"
									: `${maxLayerHeight}px`,
							}}
						>
							<Block
								full
								style={{
									overflow: "auto",
									height: "100%",
									maxHeight: isPhone
										? "65vh"
										: `${maxLayerHeight - 10}px`,
								}}
							>
								{layerSide === "top" && !isPhone && (
									<Block
										style={{
											padding: "0.5rem 1rem 0",
										}}
									>
										<StyledSelectorLabel>
											{label}
										</StyledSelectorLabel>
									</Block>
								)}
								{/* Opcjonalne pole wyszukiwania tekstowego  */}
								{withSearchInput && (
									<SearchInput
										searchValue={searchValue}
										setSearchValue={setSearchValue}
										alwaysLight={alwaysLight}
										autoFocus={autoFocus}
										isLanding={isLanding}
										customPadding={
											isPhone ? "1rem 1rem 0 1rem" : null
										}
									/>
								)}
								{/* Lista elementów do wyboru  */}
								{filteredItems.length > 0 ? (
									<MenuItems
										withSearchInput={withSearchInput}
										searchValue={searchValue}
										finalItems={finalItems}
										selected={selected}
										handleSelectItem={handleSelectItem}
										menuFontSize={menuFontSize}
										alwaysLight={alwaysLight}
										customInput={customInput}
									/>
								) : (
									<NoResultItem
										isLanding={isLanding}
										alwaysLight={alwaysLight}
										onClick={() => {
											setSearchValue("");
										}}
									/>
								)}
								{/* Opcjonalne pole  */}
								{typeof customInput === "function"
									? customInput(isOpen, extendedToggle)
									: null}
							</Block>
						</Block>
					)
				}
			>
				<DropdownTriggerContext.Consumer>
					{({ triggerProps, layerSide, isOpenSelector }) =>
						customActivator &&
						typeof customActivator === "function" ? (
							customActivator({
								innerRef: triggerProps.ref,
								toggle: extendedToggle,
								isOpen,
								contentToDisplay,
								disabled,
								layerSide,
								isOpenSelector,
							})
						) : (
							<Activator
								activatorOptions={activatorStyleOptions}
								activatorStyle={activatorStyle}
								activatorWrapperStyle={activatorWrapperStyle}
								innerRef={triggerProps.ref}
								label={label}
								onClick={() => {
									if (!isOtherSelectorInAcceptMode) {
										extendedToggle();
									}
								}}
								isPhone={isPhone}
								isOpen={isOpen}
								displayContent={contentToDisplay}
								isSelected={isSelected}
								hasSelected={isSelected}
								disabled={disabled}
								layerSide={layerSide}
								noTopMargin={activatorStyleOptions?.noTopMargin}
								noBottomMargin={
									activatorStyleOptions?.noBottomMargin
								}
								customWidth={
									isOpen
										? customWidthWhenDropdownOpen
										: undefined
								}
							/>
						)
					}
				</DropdownTriggerContext.Consumer>
			</Dropdown>
		</Block>
	);
};

// #####################################################

export default SelectorSelectSingle;

// #####################################################
